import { useCallback, useEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import type { DueItemList, DueItemsAnfrage } from "@kundeninfo/api-client";
import type { DueItemsClient } from "../termine/dueItemsClient.js";
import { dueRequestWindow } from "./timelineModel.js";
import type { TimelineWindow } from "./timelineModel.js";

/**
 * DER LADEWEG DER TERMINEBENE IN DER ZEITACHSE (Spec D11, D22, D24, Review Focus 4; Plan T19) — `listDueItems` mit dem
 * Fenster der Ansicht (über 400 Tage die ersten 400, `clipped`), den angefragten Quellen, Unternehmen und Kunde und
 * `overdue=include`. Ohne Quelle keine Abfrage und kein Stand (der Klient machte aus `sources: []` „alle Quellen“).
 *
 * Wann geladen wird: sofort bei neuer Quellenwahl, neuem Unternehmen oder Kunden und neuem `reload` (Anstoß des
 * Bereichs) sowie über `reloadNow` (Takt, Fokus, Mitternacht der Ansicht); nach einer Änderung allein des Fensters erst
 * `DUE_SETTLE_MS` später und nie während einer Geste — fällt die Frist in eine Geste, wartet das Laden auf
 * `gestureEnded`, dann beginnt die Frist neu. Ein Sofortlauf hebt eine anstehende Frist auf (Fixrunde G2: kein zweiter
 * Abruf desselben Fensters). Geladen wird immer die jüngste Anfrage; nur die jüngste Antwort zeichnet, ein Fehler lässt
 * den alten Stand stehen (`error`).
 */

/** Ruhe nach einer Fensteränderung bzw. dem Ende einer Geste, bevor die Termine neu laden (D24). */
export const DUE_SETTLE_MS = 300;

export type DueItemsOptions = {
  client: DueItemsClient;
  /** Die anzufragenden Quellen (`requestSources`); leer = keine Abfrage, kein Stand. */
  sources: readonly string[];
  /** Das Fenster der Ansicht; `null` bis zum ersten Board-Stand. */
  window: TimelineWindow | null;
  /** Unternehmen und Kunde des Filters; `null` = alle. */
  company: string | null; customer: string | null;
  /** Anstoß des Bereichs (`neuLaden`): jeder neue Wert lädt sofort. */
  reload?: number | undefined;
  /** Läuft gerade eine Geste auf dem Rollfeld? Die Ref gehört der Ansicht. */
  gesture: RefObject<boolean>;
};

export type DueItemsState = {
  due: DueItemList | null; error: boolean;
  /** Das Fenster ist länger als 400 Tage, angefragt sind die ersten 400 (Review Focus 4). */
  clipped: boolean;
  /** Sofort laden (Takt, Fokus, Mitternacht); eine anstehende Frist entfällt. */
  reloadNow: () => void;
  /** Die Geste ist zu Ende: eine wartende Fensteränderung lädt nach `DUE_SETTLE_MS`. */
  gestureEnded: () => void;
};

export function useDueItems(o: DueItemsOptions): DueItemsState {
  const [due, setDue] = useState<DueItemList | null>(null);
  const [error, setError] = useState(false);
  const lauf = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pending = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; lauf.current += 1; clearTimeout(timer.current); };
  }, []);

  const quellen = o.sources.join(",");
  const req = o.window === null ? null : dueRequestWindow(o.window);
  // Laden liest die jüngste Anfrage über eine Ref: eine Frist, die nach einer Geste abläuft, fragt das Fenster von dann
  // an, und `reloadNow` bleibt stabil (Takt und Fokus der Ansicht binden ihn nicht neu).
  const anfrage = useRef<DueItemsAnfrage | null>(null);
  anfrage.current = quellen === "" || req === null ? null
    : { from: req.from, to: req.to, sources: quellen.split(","), overdue: true,
        ...(o.company === null ? {} : { company: o.company }), ...(o.customer === null ? {} : { customer: o.customer }) };

  const { client, gesture } = o;
  const reloadNow = useCallback((): void => {
    clearTimeout(timer.current);
    pending.current = false;
    const a = anfrage.current;
    if (a === null) return;
    const nummer = ++lauf.current;
    client.listDueItems(a).then(
      (r) => { if (nummer === lauf.current && alive.current) { setDue(r); setError(false); } },
      () => { if (nummer === lauf.current && alive.current) setError(true); });
  }, [client]);
  const reloadSoon = useCallback((): void => {
    clearTimeout(timer.current);
    pending.current = true;
    timer.current = setTimeout(() => { if (!gesture.current) reloadNow(); }, DUE_SETTLE_MS);
  }, [reloadNow, gesture]);
  const gestureEnded = useCallback((): void => { if (pending.current) reloadSoon(); }, [reloadSoon]);

  const basis = `${quellen}|${o.company ?? ""}|${o.customer ?? ""}|${o.reload ?? ""}`;
  const fenster = req === null ? "" : `${req.from}|${req.to}`;
  const vorher = useRef<string | null>(null);
  useEffect(() => {
    if (quellen === "") {                                         // keine Quelle: keine Abfrage, kein Stand (H3)
      clearTimeout(timer.current);
      pending.current = false;
      lauf.current += 1;
      vorher.current = null;
      setDue(null);
      setError(false);
      return;
    }
    if (fenster === "") return;                                   // das Fenster kommt mit dem ersten Board-Stand
    const nurFenster = vorher.current === basis;
    vorher.current = basis;
    if (nurFenster) reloadSoon(); else reloadNow();
  }, [quellen, basis, fenster, reloadNow, reloadSoon]);

  return { due, error, clipped: quellen !== "" && req?.clipped === true, reloadNow, gestureEnded };
}
